import React from "react";
import { styled } from "@mui/material/styles";
import { Box, Typography } from "@mui/material";
import DefaultLayout from "../layouts/DefaultLayout";
import Seo from "./common/Seo";
import { componentMetrics } from "../theme/tokens";
import CastIcon from "@mui/icons-material/Cast";
import AirplayIcon from "@mui/icons-material/Airplay";
import appleappstore from "./Modals/app-store-logo.png";
import googleplaystore from "./Modals/google-play-logo.png";
import firetvStickPhoto from "../assets/firetv-stick-4k-plus.jpg";
// Remote only: the seller's photo puts the stick in front of a retail box
// covered in other services' logos, so it is cropped to the remote.
import strongRemotePhoto from "../assets/strong-leap-neveuk-remote.jpg";

// /bigscreen (#894): rebuilt to cover every TV FarmFlix works on, not just
// Fire TV. Copy follows app-tv/docs/supported-devices/get-farmflix-on-your-tv.md.
// Layout follows the approved mock-up (Main.dc.html). Every text and icon
// colour is named explicitly from the theme: MUI's defaults assume a light
// background (CLAUDE.md, "Styling").

const APP_STORE_URL = "https://apps.apple.com/us/app/farmflix/id1490609587";
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.farmflix&hl=en_GB&gl=US";

const pillBase = {
  textDecoration: "none",
  fontWeight: 500,
  fontSize: "1rem",
  padding: "14px 24px",
  borderRadius: componentMetrics.button.borderRadius,
  display: "inline-block",
};

const Background = styled(Box)(({ theme }) => ({
  width: "100%",
  backgroundColor: theme.palette.background.ffNavy,
}));

const Main = styled("main")(({ theme }) => ({
  maxWidth: 1040,
  margin: "0 auto",
  padding: theme.spacing(7, 2, 9),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(9),
}));

const CardGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
  gap: theme.spacing(2),
}));

const Card = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.ffNavyLight,
  borderRadius: 12,
  padding: theme.spacing(2.5),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

const NoteBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.ffNavyLighter,
  borderRadius: 12,
  padding: theme.spacing(2, 2.5),
}));

const StepNumber = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  width: 36,
  height: 36,
  borderRadius: "50%",
  backgroundColor: theme.palette.background.ffGreen,
  color: theme.palette.background.ffNavy,
  fontWeight: 700,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

type BadgeVariant = "works" | "stick" | "unsure";

const BADGE_LABEL: Record<BadgeVariant, string> = {
  works: "Works",
  stick: "Needs a stick",
  unsure: "Not sure",
};

const StatusBadge = ({ variant }: { variant: BadgeVariant }) => (
  <Box
    component="span"
    sx={{
      alignSelf: "flex-start",
      fontSize: "0.8rem",
      fontWeight: 700,
      padding: "4px 10px",
      borderRadius: "999px",
      ...(variant === "works"
        ? {
            backgroundColor: "background.ffGreen",
            color: "background.ffNavy",
          }
        : {
            border: "1px solid",
            borderColor: "common.white",
            color: "common.white",
          }),
    }}
  >
    {BADGE_LABEL[variant]}
  </Box>
);

const CardHeading = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="h3"
    sx={{ color: "common.white", fontSize: "1.1rem", fontWeight: 500 }}
  >
    {children}
  </Typography>
);

const CardMuted = ({ children }: { children: React.ReactNode }) => (
  <Typography sx={{ color: "common.darkgray", fontSize: "0.95rem" }}>
    {children}
  </Typography>
);

const CardBody = ({ children }: { children: React.ReactNode }) => (
  <Typography sx={{ color: "common.white", fontSize: "0.95rem" }}>
    {children}
  </Typography>
);

const InlineLink = styled("a")(({ theme }) => ({
  color: theme.palette.background.ffGreen,
}));

// DefaultLayout, not LoggedInLayout: this is a public guide. LoggedInLayout
// sends expired, locked and incomplete members to their account page, and
// DefaultLayout already shows the member header when someone is logged in.
const FarmFLiXBigScreen = () => {
  return (
    <DefaultLayout>
      <Seo
        title="Watch FarmFlix on your TV"
        description="Get the FarmFlix app on Amazon Fire TV, Google TV, Android TV and Apple TV, or watch on your phone, tablet or computer."
        path="/bigscreen"
      />
      <Background>
        <Main>
          {/* Hero */}
          <Box
            sx={{
              pt: 2,
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography
              variant="h1"
              sx={{
                color: "common.white",
                fontSize: "2.2rem",
                fontWeight: 700,
                "@media (min-width:600px)": { fontSize: "2.8rem" },
              }}
            >
              Watch FarmFlix on your TV
            </Typography>
            <Typography
              sx={{
                color: "common.darkgray",
                fontSize: "1.1rem",
                maxWidth: 640,
              }}
            >
              Whatever you have at home, this page will give you one of two
              answers:{" "}
              <Box component="strong" sx={{ color: "common.white" }}>
                it already works
              </Box>
              , or{" "}
              <Box component="strong" sx={{ color: "common.white" }}>
                a small plug-in stick (about £35) will make it work
              </Box>
              .
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
                justifyContent: "center",
                mt: 1,
              }}
            >
              <Box
                component="a"
                href="#find"
                sx={{
                  ...pillBase,
                  backgroundColor: "background.ffGreen",
                  color: "background.ffNavy",
                }}
              >
                Check my TV
              </Box>
              <Box
                component="a"
                href="#sticks"
                sx={{
                  ...pillBase,
                  border: "1px solid",
                  borderColor: "common.white",
                  color: "common.white",
                }}
              >
                See the sticks we recommend
              </Box>
            </Box>
          </Box>

          {/* Find what you have */}
          <Box
            id="find"
            sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
          >
            <Typography
              variant="h2"
              sx={{
                color: "common.white",
                fontSize: "1.9rem",
                fontWeight: 700,
              }}
            >
              Find what you have
            </Typography>
            <CardGrid>
              <Card>
                <StatusBadge variant="works" />
                <CardHeading>Amazon Fire TV Stick or Fire TV Cube</CardHeading>
                <CardMuted>
                  The small remote with the blue Alexa button.
                </CardMuted>
                <CardBody>
                  Press Home, search FarmFlix and install. Bought it before
                  2017? See the note below.
                </CardBody>
              </Card>

              <Card>
                <StatusBadge variant="works" />
                <CardHeading>
                  A TV that shows &quot;fire tv&quot; when it turns on
                </CardHeading>
                <CardMuted>
                  Toshiba, TCL, Hisense, JVC, Panasonic, Grundig, Xiaomi and
                  others sell these.
                </CardMuted>
                <CardBody>Press Home, search FarmFlix and install.</CardBody>
              </Card>

              <Card>
                <StatusBadge variant="works" />
                <CardHeading>A Sony smart TV (2015 or newer)</CardHeading>
                <CardBody>
                  Press Home, search FarmFlix in the apps and install.
                </CardBody>
              </Card>

              <Card>
                <StatusBadge variant="works" />
                <CardHeading>
                  A TV that shows &quot;Google TV&quot; or &quot;Android
                  TV&quot; when it turns on
                </CardHeading>
                <CardMuted>
                  TCL, Sharp, Philips, JVC, Toshiba, Bush, Logik, Polaroid,
                  Technika and most supermarket own brands.
                </CardMuted>
                <CardBody>
                  Press Home, search FarmFlix in the apps and install.
                </CardBody>
              </Card>

              <Card>
                <StatusBadge variant="works" />
                <CardHeading>An Apple TV box</CardHeading>
                <CardMuted>The small black Apple box.</CardMuted>
                <CardBody>
                  Open the App Store on it, search FarmFlix and install.
                </CardBody>
              </Card>

              <Card>
                <StatusBadge variant="stick" />
                <CardHeading>A Samsung or LG smart TV</CardHeading>
                <CardBody>
                  Use one of the{" "}
                  <InlineLink href="#sticks">sticks below</InlineLink>. It plugs
                  into the back of the TV and takes about ten minutes to set up.
                </CardBody>
                <CardMuted>
                  On newer Samsung and LG TVs, a phone can also send video
                  straight to the TV.{" "}
                  <InlineLink href="#phone">See how</InlineLink>.
                </CardMuted>
              </Card>

              <Card>
                <StatusBadge variant="stick" />
                <CardHeading>
                  Roku, Sky Glass, a Sky box, or an older TV
                </CardHeading>
                <CardBody>
                  Use one of the{" "}
                  <InlineLink href="#sticks">sticks below</InlineLink>. Any TV
                  with an HDMI socket in the back can take one, however old.
                </CardBody>
              </Card>

              <Card>
                <StatusBadge variant="unsure" />
                <CardHeading>Not sure what you have</CardHeading>
                <CardBody>
                  Use one of the{" "}
                  <InlineLink href="#sticks">sticks below</InlineLink>. They
                  work with any TV that has an HDMI socket, and they are what we
                  recommend anyway.
                </CardBody>
              </Card>
            </CardGrid>

            <NoteBox>
              <CardBody>
                <Box component="strong" sx={{ color: "common.white" }}>
                  Older Amazon stick?
                </Box>{" "}
                If your Fire TV Stick was bought before 2017, FarmFlix still
                works on it, but you will have our classic app, which won&apos;t
                get new features. A current stick gives you the new app and is
                worth the upgrade.
              </CardBody>
            </NoteBox>
          </Box>

          {/* The sticks we recommend */}
          <Box
            id="sticks"
            sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography
                variant="h2"
                sx={{
                  color: "common.white",
                  fontSize: "1.9rem",
                  fontWeight: 700,
                }}
              >
                The sticks we recommend
              </Typography>
              <Typography
                sx={{ color: "common.darkgray", fontSize: "1.05rem" }}
              >
                If your TV isn&apos;t on the list, or you want the best FarmFlix
                experience, plug one of these into the back of it. We test
                FarmFlix on both, so we know they work. Either one is a good
                choice, so pick whichever is on offer.
              </Typography>
            </Box>
            <CardGrid>
              <Card sx={{ padding: 3 }}>
                <Box
                  component="img"
                  src={firetvStickPhoto}
                  alt="Amazon Fire TV Stick 4K Plus and remote"
                  sx={{
                    height: 180,
                    width: "100%",
                    objectFit: "contain",
                    backgroundColor: "common.white",
                    borderRadius: 1,
                    display: "block",
                  }}
                />
                <CardHeading>Amazon Fire TV Stick</CardHeading>
                <CardMuted>
                  We recommend the Fire TV Stick 4K Plus. Any current Fire TV
                  Stick works, roughly £35 to £60 at Amazon, Argos or Currys.
                </CardMuted>
                <Box
                  component="a"
                  href="https://www.amazon.co.uk/dp/B0F7ZFCZNL"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    ...pillBase,
                    alignSelf: "flex-start",
                    backgroundColor: "background.ffGreen",
                    color: "background.ffNavy",
                  }}
                >
                  See the Fire TV Stick 4K Plus on Amazon
                </Box>
              </Card>

              <Card sx={{ padding: 3 }}>
                <Box
                  component="img"
                  src={strongRemotePhoto}
                  alt="STRONG LEAP-NEVEUK Google TV remote"
                  sx={{
                    height: 180,
                    width: "100%",
                    objectFit: "contain",
                    backgroundColor: "common.white",
                    borderRadius: 1,
                    display: "block",
                  }}
                />
                <CardHeading>A Google TV stick</CardHeading>
                <CardMuted>
                  We test on the STRONG LEAP-NEVEUK Google TV stick. Any stick
                  or box that shows &quot;Google TV&quot; when it turns on will
                  work, including Google&apos;s own Google TV Streamer. You can
                  also send video to it from the FarmFlix app on your phone.
                </CardMuted>
                <Box
                  component="a"
                  href="https://www.amazon.co.uk/dp/B0G47HDQ9N"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    ...pillBase,
                    alignSelf: "flex-start",
                    backgroundColor: "background.ffGreen",
                    color: "background.ffNavy",
                  }}
                >
                  See the STRONG LEAP-NEVEUK on Amazon
                </Box>
              </Card>
            </CardGrid>
            <Typography sx={{ color: "common.darkgray", fontSize: "0.9rem" }}>
              Looking at a Google Chromecast? Make sure it is a model with a
              remote: the older Chromecasts without a remote can&apos;t run
              apps.
            </Typography>
          </Box>

          {/* Setting up a stick */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Typography
              variant="h2"
              sx={{
                color: "common.white",
                fontSize: "1.9rem",
                fontWeight: 700,
              }}
            >
              Setting up a stick
            </Typography>
            <CardGrid
              sx={{
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              }}
            >
              <Card
                sx={{
                  flexDirection: "row",
                  alignItems: "flex-start",
                  gap: 1.75,
                }}
              >
                <StepNumber>1</StepNumber>
                <CardBody>
                  Plug it into an HDMI socket in the back of the TV and into the
                  mains.
                </CardBody>
              </Card>
              <Card
                sx={{
                  flexDirection: "row",
                  alignItems: "flex-start",
                  gap: 1.75,
                }}
              >
                <StepNumber>2</StepNumber>
                <CardBody>
                  Follow the on-screen steps to join your wifi.
                </CardBody>
              </Card>
              <Card
                sx={{
                  flexDirection: "row",
                  alignItems: "flex-start",
                  gap: 1.75,
                }}
              >
                <StepNumber>3</StepNumber>
                <CardBody>
                  Press Home, search FarmFlix, install it and sign in.
                </CardBody>
              </Card>
            </CardGrid>
            <Card>
              <CardHeading>Signing in is simple</CardHeading>
              <CardMuted>
                The TV shows a short code. Open the FarmFlix app on your phone
                and it signs you in. There is no need to type your password with
                the remote.
              </CardMuted>
            </Card>
          </Box>

          {/* Sending video from your phone to the TV */}
          <Box
            id="phone"
            sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
          >
            <Typography
              variant="h2"
              sx={{
                color: "common.white",
                fontSize: "1.9rem",
                fontWeight: 700,
              }}
            >
              Sending video from your phone to the TV
            </Typography>
            <Typography sx={{ color: "common.darkgray", fontSize: "1.05rem" }}>
              If you already watch on your phone, look for these buttons in the
              video player. Your phone and TV need to be on the same wifi.
            </Typography>
            <CardGrid>
              <Card
                sx={{
                  flexDirection: "row",
                  alignItems: "flex-start",
                  gap: 1.75,
                }}
              >
                <CastIcon
                  sx={{
                    color: "background.ffGreen",
                    fontSize: 32,
                    flexShrink: 0,
                  }}
                />
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  <CardHeading>Cast</CardHeading>
                  <CardMuted>
                    Sends the video to a Chromecast or any TV with Chromecast
                    built in.
                  </CardMuted>
                </Box>
              </Card>
              <Card
                sx={{
                  flexDirection: "row",
                  alignItems: "flex-start",
                  gap: 1.75,
                }}
              >
                <AirplayIcon
                  sx={{
                    color: "background.ffGreen",
                    fontSize: 32,
                    flexShrink: 0,
                  }}
                />
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  <CardHeading>AirPlay (iPhone and iPad)</CardHeading>
                  <CardMuted>
                    Sends it to an Apple TV, or to many newer Samsung, LG and
                    Sony TVs.
                  </CardMuted>
                </Box>
              </Card>
            </CardGrid>
            <NoteBox sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <CardHeading>
                Why the app on the TV works better than casting
              </CardHeading>
              <Typography
                sx={{ color: "common.lightgray", fontSize: "0.95rem" }}
              >
                When you cast, the video is played by your TV&apos;s own casting
                software, not by FarmFlix. Each TV maker&apos;s version is
                slightly different, and we can&apos;t see into it or fix it,
                which is why some viewers find casting stutters or pauses on
                their TV. When the FarmFlix app is installed on the TV or a
                stick, we play the video ourselves, and if something goes wrong
                we can fix it in the next update. Casting is fine for the
                occasional video, but if it keeps buffering on your TV, or you
                watch FarmFlix regularly, the app on a stick is the more
                reliable way to watch.
              </Typography>
            </NoteBox>
          </Box>

          {/* Wherever you are */}
          <Box
            sx={{
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.5,
              borderTop: "1px solid",
              borderColor: "background.ffNavyLighter",
              pt: 6,
            }}
          >
            <Typography
              variant="h2"
              sx={{
                color: "common.white",
                fontSize: "1.7rem",
                fontWeight: 700,
              }}
            >
              Wherever you are
            </Typography>
            <Typography
              sx={{
                color: "common.darkgray",
                fontSize: "1.05rem",
                maxWidth: 600,
              }}
            >
              FarmFlix is also on your phone and tablet, and on your computer at
              farmflix.tv. All of these are included in your membership at no
              extra cost.
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.25,
                justifyContent: "center",
              }}
            >
              <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                <Box
                  component="img"
                  src={appleappstore}
                  alt="Download on the App Store"
                  sx={{ height: 48, width: "auto", display: "block" }}
                />
              </a>
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Box
                  component="img"
                  src={googleplaystore}
                  alt="Get it on Google Play"
                  sx={{ height: 48, width: "auto", display: "block" }}
                />
              </a>
            </Box>
          </Box>
        </Main>
      </Background>
    </DefaultLayout>
  );
};

export default FarmFLiXBigScreen;
